import { FC, Fragment, useCallback, useMemo } from 'react';
import { H3 } from 'components/Typography';
import { BKJButton, Flex } from '@bookjane2/bookjane-design-library';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { useEditViewContext } from 'modals/ShiftModal/src/views/EditView/EditView.context';
import { AutoMargin } from 'components/AutoMargin';
import { useTranslation } from 'hooks/useTranslation';
import {
  ConfirmEditsContainer,
  ConfirmEditsHeader,
  ConfirmEditsRow,
  ReasonForEditingBody,
  ReasonForEditingHeader,
  ZeroHoursWorked,
} from 'modals/ShiftModal/src/views/ConfirmEditsView/ConfirmEditsView.styled';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import { breakTimeToWords, formatTimeFromISO } from 'utils/time';
import { reasonForEditsSelectOptions } from 'modals/ShiftModal/src/views/ReasonForEdits/ReasonForEdits.constants';
import { useSelector } from 'react-redux';
import { FEATURE_SELECTORS_MAP } from 'guards/FeatureGuard.constants';
import { DateTime } from 'luxon';
import isUserTimeFormat24hSelector from 'store/selectors/userSelectors/isUserTimeFormat24hSelector';

export const ConfirmEditsView: FC = () => {
  const { t } = useTranslation();
  const {
    isSubmitting,
    onSubmit,
    values: {
      start_date,
      start_time,
      end_time,
      unpaid_break_time,
      description,
      reasonForEdit,
      zeroHoursWorked,
      customFields,
      isNotifyFlowRequired,
      positionId,
      byMultipleFields,
      teamMemberName,
      janeId,
      positionName,
      ...restValues
    },
    hasCustomers,
    selectedCustomer,
  } = useEditViewContext();

  const { data } = useShiftModalDataContext();
  const order = data?.order;

  const communityCustomFields = order.availableCustomFields || [];
  const timeZone = order.timeZone?.toString();

  const isCustomFieldsEnabled =
    useSelector(FEATURE_SELECTORS_MAP['FEATURE_TYPE_CUSTOM_FIELDS']) &&
    !order?.isExternal &&
    communityCustomFields.length > 0;
  const is24hTimeFormat = useSelector(isUserTimeFormat24hSelector);

  const reasonForEditLabel = useMemo(
    () =>
      reasonForEditsSelectOptions().find((reasonOption) => reasonOption.value === reasonForEdit[0])
        ?.label,
    [reasonForEdit],
  );

  const handleSubmit = useCallback(
    (values) => {
      const submitValues = {
        ...values,
        ...restValues,
        start_date: zeroHoursWorked ? order.startDate : start_date,
        start_time: zeroHoursWorked ? order.startAt : start_time,
        end_time: zeroHoursWorked ? order.endAt : end_time,
        unpaid_break_time: zeroHoursWorked ? order.orderSchedule.breakTime : unpaid_break_time,
      };
      return onSubmit(submitValues);
    },
    [
      order.endAt,
      order.orderSchedule.breakTime,
      order.startAt,
      order.startDate,
      end_time,
      onSubmit,
      restValues,
      start_date,
      start_time,
      unpaid_break_time,
      zeroHoursWorked,
    ],
  );

  const startDateLabel = DateTime.fromISO(order.startDate).toFormat('EEE, MMM dd, yyyy');
  const timeFormat = is24hTimeFormat ? 'HH:mm' : 'h:mm a';
  const startTimeLabel = formatTimeFromISO(order.startAt, is24hTimeFormat, timeZone);
  const endTimeLabel = formatTimeFromISO(order.endAt, is24hTimeFormat, timeZone);
  const breakTimeLabel = breakTimeToWords(order.orderSchedule.breakTime);

  const formatTimeFromString = (timeString: string): string => {
    const formatted = DateTime.fromFormat(timeString, 'HH:mm:ss').toFormat(timeFormat);
    return is24hTimeFormat ? formatted : formatted.toLowerCase();
  };

  return (
    <Fragment>
      <Flex flexDirection="column" padding="32px" flex="1">
        <Flex padding="0 0 30px 0">
          <H3 color="TextDarkGrey">{t('shift_modal:CONFIRM_EDITS')}</H3>
        </Flex>
        <Flex padding="0 0 15px 0">
          <div>{t('shift_modal:PLEASE_CONFIRM_FOLLOWING_EDITS')}</div>
        </Flex>
        <ConfirmEditsContainer>
          <ConfirmEditsHeader>{t('shift_modal:ITEM')}</ConfirmEditsHeader>
          <ConfirmEditsHeader>{t('shift_modal:CURRENT')}</ConfirmEditsHeader>
          <ConfirmEditsHeader>{t('shift_modal:EDITED_TO')}</ConfirmEditsHeader>

          <ConfirmEditsRow>{t('shift_modal:POSITION')}</ConfirmEditsRow>
          <ConfirmEditsRow>{order.position.name}</ConfirmEditsRow>
          <ConfirmEditsRow>{positionName || '-'}</ConfirmEditsRow>

          <ConfirmEditsRow>{t('shift_modal:TEAM_MEMBER')}</ConfirmEditsRow>
          <ConfirmEditsRow>
            {order.mainJane ? `${order.mainJane.firstName} ${order.mainJane.lastName}` : '-'}
          </ConfirmEditsRow>
          <ConfirmEditsRow>{teamMemberName || '-'}</ConfirmEditsRow>

          <ConfirmEditsRow>{t('shift_modal:DATE')}</ConfirmEditsRow>
          <ConfirmEditsRow>{startDateLabel}</ConfirmEditsRow>
          <ConfirmEditsRow>
            {zeroHoursWorked
              ? startDateLabel
              : DateTime.fromISO(start_date[0]).toFormat('EEE, MMM dd, yyyy')}
          </ConfirmEditsRow>

          <ConfirmEditsRow $isEven>{t('shift_modal:START_TIME')}</ConfirmEditsRow>
          <ConfirmEditsRow $isEven>{startTimeLabel}</ConfirmEditsRow>
          <ConfirmEditsRow $isEven>
            {zeroHoursWorked ? t('shift_modal:ABSENT') : formatTimeFromString(start_time[0])}
          </ConfirmEditsRow>

          <ConfirmEditsRow>{t('shift_modal:END_TIME')}</ConfirmEditsRow>
          <ConfirmEditsRow>{endTimeLabel}</ConfirmEditsRow>
          <ConfirmEditsRow>
            {zeroHoursWorked ? t('shift_modal:ABSENT') : formatTimeFromString(end_time[0])}
          </ConfirmEditsRow>

          <ConfirmEditsRow $isEven>{t('common:UNPAID_BREAK_TIME')}</ConfirmEditsRow>
          <ConfirmEditsRow $isEven>{breakTimeLabel}</ConfirmEditsRow>
          <ConfirmEditsRow $isEven>
            {zeroHoursWorked ? t('shift_modal:ABSENT') : breakTimeToWords(unpaid_break_time[0])}
          </ConfirmEditsRow>

          {hasCustomers && (
            <Fragment>
              <ConfirmEditsRow>{t('common:CUSTOMER')}</ConfirmEditsRow>
              <ConfirmEditsRow>{order.subFacilityAddress?.name}</ConfirmEditsRow>
              <ConfirmEditsRow>{selectedCustomer?.name}</ConfirmEditsRow>
            </Fragment>
          )}

          <ConfirmEditsRow>{t('shift_modal:NOTES')}</ConfirmEditsRow>
          <ConfirmEditsRow>{order.description}</ConfirmEditsRow>
          <ConfirmEditsRow>{description}</ConfirmEditsRow>

          {isCustomFieldsEnabled &&
            communityCustomFields.map((customField, index) => (
              <Fragment key={index}>
                <ConfirmEditsRow>{customField.name}</ConfirmEditsRow>
                <ConfirmEditsRow>
                  {
                    order?.customFields?.find(
                      (orderCustomField) => orderCustomField.name === customField.name,
                    )?.value
                  }
                </ConfirmEditsRow>
                <ConfirmEditsRow>{restValues[customField.name]}</ConfirmEditsRow>
              </Fragment>
            ))}
        </ConfirmEditsContainer>
        {reasonForEditLabel && (
          <Flex padding="40px 0 4px 0">
            <ReasonForEditingHeader>
              {t('shift_modal:REASON_FOR_EDITING_1')}:
            </ReasonForEditingHeader>
          </Flex>
        )}
        {reasonForEditLabel && (
          <Flex>
            <ReasonForEditingBody>{reasonForEditLabel}</ReasonForEditingBody>
          </Flex>
        )}
        {zeroHoursWorked && (
          <Flex padding="30px 0 0 0" flexDirection="column" gap="8px">
            <ZeroHoursWorked>{t('shift_modal:ABSENT')}</ZeroHoursWorked>
            <ReasonForEditingBody>
              {t('shift_modal:ZERO_HOURS_WORKED_WARNING')}
            </ReasonForEditingBody>
          </Flex>
        )}
        {isNotifyFlowRequired && (
          <Flex padding="40px 0 0 0">
            {t('shift_modal:FOLLOWING_TEAM_MEMBERS_EMAIL_NOTIFICATION')}
          </Flex>
        )}
      </Flex>

      <BKJSideModalFooter justifyContent="space-between">
        <AutoMargin />
        <BKJButton
          variant="PurpleSolid"
          width="180px"
          type="button"
          disabled={false}
          isLoading={isSubmitting}
          onClick={handleSubmit}
        >
          {t('shift_modal:PUBLISH_EDITS')}
        </BKJButton>
      </BKJSideModalFooter>
    </Fragment>
  );
};
